import { useEffect, useState } from "react";
import { Eye, FileText, Loader2, Sparkles, Timer, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { openPdf } from "@/components/AuthImg";
import TestPaperReview from "@/components/admin/TestPaperReview";

const GRADES = [1, 2, 3, 4, 5, 6];
const sel = "bg-slate-900 border-2 border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100";
const STATUS = {
  none: ["Download only", "bg-slate-700 text-slate-200"],
  working: ["Preparing online version…", "bg-blue-900/70 text-blue-200"],
  review: ["Online version: check & publish", "bg-amber-900/70 text-amber-200"],
  published: ["Online for children", "bg-green-900/70 text-green-200"],
  failed: ["Online version failed", "bg-red-900/70 text-red-200"],
};

function UploadForm({ subjects, onDone }) {
  const [f, setF] = useState({ subject: "Maths", grade: "4", title: "", duration_min: "" });
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!paper) return toast.error("Choose the paper PDF");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => v !== "" && fd.append(k, v));
    fd.append("paper", paper);
    if (answers) fd.append("answers", answers);
    setBusy(true);
    try { await api.post("/admin/test-papers", fd); toast.success("Test paper uploaded"); setF({ ...f, title: "" }); setPaper(null); setAnswers(null); e.target.reset(); onDone(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4 grid gap-3 md:grid-cols-3" data-testid="tp-upload-form">
      <select value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={sel} data-testid="tp-upload-subject">{subjects.map((s) => <option key={s}>{s}</option>)}</select>
      <select value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} className={sel} data-testid="tp-upload-grade">{GRADES.map((g) => <option key={g} value={g}>Grade {g}</option>)}</select>
      <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title, e.g. Term 1 test 2025" className={sel} data-testid="tp-upload-title" />
      <label className="text-sm text-slate-300">Paper (PDF)<input type="file" accept="application/pdf" required onChange={(e) => setPaper(e.target.files[0])} className="block mt-1 text-xs" data-testid="tp-upload-paper" /></label>
      <label className="text-sm text-slate-300">Answer key (PDF, optional)<input type="file" accept="application/pdf" onChange={(e) => setAnswers(e.target.files[0])} className="block mt-1 text-xs" data-testid="tp-upload-answers" /></label>
      <label className="text-sm text-slate-300">Exam time in minutes (optional)
        <input type="number" min="0" max="240" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} placeholder="e.g. 45" className={`${sel} block mt-1 w-28`} data-testid="tp-upload-duration" />
      </label>
      <button disabled={busy} className="self-end px-4 py-2 rounded-full bg-yellow-300 text-slate-900 font-bold inline-flex items-center justify-center gap-2" data-testid="tp-upload-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
      </button>
    </form>
  );
}

function TimeEdit({ p, onChanged }) {
  const [v, setV] = useState(p.duration_min ?? "");
  const save = async () => {
    const n = v === "" ? null : Number(v);
    if (n === (p.duration_min ?? null)) return;
    try { await api.patch(`/admin/test-papers/${p.id}`, { title: p.title, subject: p.subject, grade: p.grade, duration_min: n }); toast.success(n ? `Timer: ${n} min` : "No timer"); onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-slate-300" data-testid="tp-row-duration">
      <Timer size={14} /> Time
      <input type="number" min="0" max="240" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        placeholder="—" className="w-16 bg-slate-900 border-2 border-slate-600 rounded-lg px-2 py-1 text-slate-100" data-testid="tp-row-duration-input" /> min
    </label>
  );
}

function PaperRow({ p, onChanged, onReview }) {
  const [label, cls] = STATUS[p.online_status] || STATUS.none;
  const act = (fn, ok) => async () => { try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } };
  const addKey = async (file) => { const fd = new FormData(); fd.append("file", file); await act(() => api.post(`/admin/test-papers/${p.id}/answers`, fd), "Answer key added")(); };
  const ready = ["review", "published"].includes(p.online_status);
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4" data-testid={`tp-row-${p.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <FileText className="text-yellow-300" size={20} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-100" data-testid="tp-row-title">{p.title}</p>
          <p className="text-xs text-slate-400">{p.subject} · Grade {p.grade} · {p.pages} page(s) · {p.downloads} download(s){p.has_answers ? " · answer key ✓" : " · no answer key"}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`} data-testid="tp-row-status">{label}{ready ? ` · ${p.questions} q` : ""}</span>
      </div>
      {p.online_status === "failed" && p.online_error && <p className="text-xs text-red-300 mt-2">{p.online_error}</p>}
      <div className="flex flex-wrap gap-2 mt-3 text-sm">
        <button onClick={() => openPdf(`/admin/test-papers/${p.id}/file/paper`)} className="px-3 py-1 rounded-full border-2 border-slate-600 inline-flex items-center gap-1" data-testid="tp-row-view"><Eye size={14} /> Paper</button>
        {p.has_answers && <button onClick={() => openPdf(`/admin/test-papers/${p.id}/file/answers`)} className="px-3 py-1 rounded-full border-2 border-slate-600 inline-flex items-center gap-1" data-testid="tp-row-view-answers"><Eye size={14} /> Answer key</button>}
        <label className="px-3 py-1 rounded-full border-2 border-slate-600 cursor-pointer inline-flex items-center gap-1" data-testid="tp-row-add-answers">
          <Upload size={14} /> {p.has_answers ? "Replace answer key" : "Add answer key"}
          <input type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && addKey(e.target.files[0])} />
        </label>
        {p.online_status !== "working" && (
          <button onClick={act(async () => { if (!ready || window.confirm("Read the PDF again? Your checked questions will be replaced.")) await api.post(`/admin/test-papers/${p.id}/extract`); }, null)}
            className="px-3 py-1 rounded-full bg-blue-600 text-white inline-flex items-center gap-1" data-testid="tp-row-extract">
            <Sparkles size={14} /> {p.online_status === "none" ? "Make online version" : "Read the PDF again"}
          </button>
        )}
        {ready && <button onClick={onReview} className="px-3 py-1 rounded-full bg-amber-300 text-slate-900 font-bold" data-testid="tp-row-review">Check questions</button>}
        <TimeEdit p={p} onChanged={onChanged} />
        <button onClick={act(async () => { if (window.confirm(`Delete "${p.title}"?`)) await api.delete(`/admin/test-papers/${p.id}`); }, null)}
          className="ml-auto px-3 py-1 rounded-full text-red-300 inline-flex items-center gap-1" data-testid="tp-row-delete"><Trash2 size={14} /> Delete</button>
      </div>
    </div>
  );
}

export default function TestPapersAdmin() {
  const [data, setData] = useState(null);
  const [grade, setGrade] = useState("all");
  const [subject, setSubject] = useState("all");
  const [review, setReview] = useState(null);
  const load = () => api.get("/admin/test-papers").then(({ data }) => setData(data)).catch(() => setData({ papers: [], subjects: [] }));
  useEffect(() => { load(); }, []);
  const working = data?.papers.some((p) => p.online_status === "working");
  useEffect(() => { if (!working) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [working]);
  if (!data) return <Loader2 className="animate-spin text-yellow-300" />;
  const list = data.papers.filter((p) => (grade === "all" || String(p.grade) === grade) && (subject === "all" || p.subject === subject));
  return (
    <div className="space-y-4" data-testid="admin-test-papers">
      <p className="text-sm text-slate-400">Upload a PDF test paper per subject and grade. Parents who pay for that subject can download it (with their name on every page). Use “Make online version” so children can also do it on screen.</p>
      <UploadForm subjects={data.subjects} onDone={load} />
      <div className="flex flex-wrap gap-2">
        <select value={grade} onChange={(e) => setGrade(e.target.value)} className={sel} data-testid="tp-filter-grade"><option value="all">All grades</option>{GRADES.map((g) => <option key={g} value={String(g)}>Grade {g}</option>)}</select>
        <select value={subject} onChange={(e) => setSubject(e.target.value)} className={sel} data-testid="tp-filter-subject"><option value="all">All subjects</option>{data.subjects.map((s) => <option key={s}>{s}</option>)}</select>
        <span className="self-center text-sm text-slate-400" data-testid="tp-count">{list.length} paper(s)</span>
      </div>
      {list.length === 0 && <p className="text-slate-400 text-center py-8" data-testid="tp-empty">No test paper yet.</p>}
      {list.map((p) => (
        <div key={p.id}>
          <PaperRow p={p} onChanged={load} onReview={() => setReview(review === p.id ? null : p.id)} />
          {review === p.id && <TestPaperReview pid={p.id} onClose={() => setReview(null)} onChanged={load} />}
        </div>
      ))}
    </div>
  );
}
